import type { WorkItem } from "../types/domain";

export interface WorkItemNode extends WorkItem {
  children: WorkItemNode[];
}

export function buildWorkItemTree(items: readonly WorkItem[]): WorkItemNode[] {
  const nodes = new Map<string, WorkItemNode>();
  for (const item of items) {
    nodes.set(item.id, { ...item, children: [] });
  }

  const roots: WorkItemNode[] = [];
  for (const node of nodes.values()) {
    if (node.parentId === null) {
      roots.push(node);
      continue;
    }
    const parent = nodes.get(node.parentId);
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const sortRecursively = (node: WorkItemNode): void => {
    node.children.sort(
      (left, right) => left.sortOrder - right.sortOrder || left.title.localeCompare(right.title),
    );
    node.children.forEach(sortRecursively);
  };

  roots.sort(
    (left, right) => left.sortOrder - right.sortOrder || left.title.localeCompare(right.title),
  );
  roots.forEach(sortRecursively);
  return roots;
}

export function getDescendantIds(items: readonly WorkItem[], rootId: string): Set<string> {
  const children = new Map<string, string[]>();
  for (const item of items) {
    if (!item.parentId) continue;
    const bucket = children.get(item.parentId) ?? [];
    bucket.push(item.id);
    children.set(item.parentId, bucket);
  }

  const result = new Set<string>();
  const queue = [...(children.get(rootId) ?? [])];
  while (queue.length > 0) {
    const id = queue.shift();
    if (!id || result.has(id)) continue;
    result.add(id);
    queue.push(...(children.get(id) ?? []));
  }
  return result;
}

export function isLeaf(item: WorkItem, items: readonly WorkItem[]): boolean {
  return !items.some((candidate) => candidate.parentId === item.id);
}
